import {map} from 'rxjs/operators';
import {AutoUnsubscribe} from '../../../../decorators/auto-unsubscribe';
import {Injectable} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {DocumentFileService} from '../../../../services/document-file.service';
import {ReviewQueueService} from '../../../../services/review-queue.service';
import {DocumentFile} from '../../../../models/document-file';
import {SpreadingState} from '../state/manager';
import {ReviewQueueItem} from '../../../../models/review-queue-item';
import {Cell, Row} from '../models/models';
import {Statement} from '../../review/human-validation/models/statement';
import {StatementService} from '../../../../services/statement.service';
import {SpreadingTemplateService} from '../../../../services/spreading-template.service';
import {SpreadingMapTemplates} from '../../../../models/standard-item-templates';
import {
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  INCOME_STATEMENT,
  TAXONOMY_ITEM_IDS
} from '../../../../utils/constants';
import {StatementDataCache} from './cache';
import {LoadTaxonomyResponse, TemplateItem} from "@models/template-item";
import {CommentSaveMetadata} from "@services/shared/comment/comment.service";

interface TaxonomyItemWithLineId extends TemplateItem {
  lineItemId?: number;
  children: TaxonomyItemWithLineId[];
}

@AutoUnsubscribe('subsArr$')
@Injectable()
export class StatementDataService {
  subsArr$: Subscription[] = [];

  constructor(
    private _documentFileService: DocumentFileService,

    // NOTE: the functionality from here should be moved to a new service
    // called "TemplateService" or something.
    private _reviewQueueService: ReviewQueueService,
    private _statementService: StatementService,
    private _spreadingTemplateService: SpreadingTemplateService,

  ) { }

  loadState(statement: Statement, cache: StatementDataCache = null): Promise<SpreadingState> {
    return Promise.all([
      this.loadTaxonomy(cache, statement.spreadingTemplateId),
      this.loadDocumentFile(statement.documentFileId, cache),
      this.loadReviewQueueItem(statement.documentFileId, cache),
    ]).then(results => {
      return this.loadStateSuccess(results, statement, cache);
    }).catch((err) => {
      console.log('err', err)
      throw err
    });
  }

  loadStateSuccess(results: any, statement: Statement, cache: StatementDataCache): SpreadingState {
      const template = this.getTemplateForStatement(results[0], statement);
      const state = SpreadingState.newFromStatement(statement, template);
      state.reviewQueueItem = results[2];
      state.documentFile = results[1];
      state.taxonomy = results[0].taxonomy;

      if (cache) {
        cache.spreadingTaxonomyBySpreadingTemplateId.set(statement.spreadingTemplateId, results[0]);
        cache.reviewQueueItemIDsByFileId.set(state.documentFile.id, state.reviewQueueItem.id);
      }
      return state;
  }

  loadDocumentFile(fileId: number, cache: StatementDataCache = null): Promise<DocumentFile> {
    if (cache && cache.documentFilesById.has(fileId)) {
      return Promise.resolve(cache.documentFilesById.get(fileId));
    }
    return firstValueFrom(this._documentFileService.listDocumentFiles(null, fileId, true, null, false, false, true).pipe(map(data => {
      return data[0];
    })));
  }

  loadStatementsForFile(fileId: number, cache: StatementDataCache = null): Promise<Array<Statement>> {
    if (cache && cache.relatedStatementsByFileId.has(fileId)) {
      return Promise.resolve(cache.relatedStatementsByFileId.get(fileId));
    }
    return firstValueFrom(this._documentFileService.loadStatements(fileId)).then(statements => {
      if (cache) {
        cache.relatedStatementsByFileId.set(fileId, statements);
      }

      return statements;
    });
  }

  loadStatementById(statementId): Promise<Statement> {
    return firstValueFrom(this._statementService.loadStatement(statementId));
  }

  loadStatementByUuid(statementUuid): Promise<Statement> {
    return firstValueFrom(this._statementService.loadStatementByUuid(statementUuid));
  }

  loadStatementsByIds(statementIds: number[]): Promise<Statement[]> {
    return firstValueFrom(this._statementService.loadStatementsByIds(statementIds));
  }

  /**
   * WARNING!!! We will need to change this to actually be by borrower once we
   * implement custom templates`
   */
  loadTaxonomy(cache: StatementDataCache = null, spreadingTemplateId: number): Promise<LoadTaxonomyResponse> {
    if (cache && cache.spreadingTaxonomyBySpreadingTemplateId.has(spreadingTemplateId)) {
      return Promise.resolve(cache.spreadingTaxonomyBySpreadingTemplateId.get(spreadingTemplateId));
    }
    return firstValueFrom(this._spreadingTemplateService.getSpreadingTaxonomyById(spreadingTemplateId));
  }

  loadReviewQueueItem(fileId: number, cache: StatementDataCache = null): Promise<ReviewQueueItem> {
    let retrieveReviewQueueItemPromise: Promise<string>;

    if (cache && cache.reviewQueueItemIDsByFileId.has(fileId)) {
      retrieveReviewQueueItemPromise = Promise.resolve(cache.reviewQueueItemIDsByFileId.get(fileId));
    } else {
      retrieveReviewQueueItemPromise = firstValueFrom(this._documentFileService.getReviewQueueItemForFileId(fileId)).then(data => data.response.objects[0].reviewQueueItem);
    }
    return retrieveReviewQueueItemPromise.then((reviewQueueItemId: string) => {
      return firstValueFrom(this._reviewQueueService.retrieveAndLock(reviewQueueItemId));
    });
  }

  loadStateForValidation(statement: Statement, cache: StatementDataCache = null): Promise<SpreadingState> {
    return Promise.all([
      this.loadTaxonomy(cache, statement.spreadingTemplateId),
    ]).then(results => {
      return this.loadStateSuccessForValidation(results[0], statement, cache);
    }).catch((err) => {
      console.log('err', err)
      throw err
    });
  }

  loadStateSuccessForValidation(results: LoadTaxonomyResponse, statement: Statement, cache: StatementDataCache): SpreadingState {
      // For validation, we need the template to get the ref values for submission rules
      // but we can optimize by only loading the minimal template structure needed
      const template = this.getTemplateForStatement(results, statement);
      const state = SpreadingState.newFromStatement(statement, template);
      state.taxonomy = results.taxonomy;
      state.statementType = statement.statementType;
      if (cache) {
        cache.spreadingTaxonomyBySpreadingTemplateId.set(statement.spreadingTemplateId, results);
      }
      return state;
  }

  private getTemplateForStatement(taxonomyResult: LoadTaxonomyResponse, statement: Statement): Array<Row> {
      const tmpl = new SpreadingMapTemplates();
      const taxonomy = taxonomyResult.taxonomy as TaxonomyItemWithLineId[];
      tmpl.incomeStatementTmpl = taxonomy?.find(taxonomyItem => taxonomyItem.lineItemId === TAXONOMY_ITEM_IDS.INCOME_STATEMENT)?.children as Array<Row>;
      tmpl.balanceSheetTmpl = taxonomy?.find(taxonomyItem => taxonomyItem.lineItemId === TAXONOMY_ITEM_IDS.BALANCE_SHEET)?.children as Array<Row>;
      
      // Grab Cash Flow if its set
      if (taxonomy?.length > 2) {
        const cashFlowTaxonomyItm = taxonomy?.find(taxonomyItem => taxonomyItem.lineItemId === TAXONOMY_ITEM_IDS.CASH_FLOW_STATEMENT);
        tmpl.cashFlowStatementTmpl = cashFlowTaxonomyItm?.lineItemId === TAXONOMY_ITEM_IDS.CASH_FLOW_STATEMENT ? cashFlowTaxonomyItm?.children as Array<Row> : [];
      }

      if (statement.statementType === INCOME_STATEMENT && !!tmpl.incomeStatementTmpl) {
        return tmpl.incomeStatementTmpl;
      } else if (statement.statementType === BALANCE_SHEET && !!tmpl.balanceSheetTmpl) {
        return tmpl.balanceSheetTmpl;
      } else if (statement.statementType === CASH_FLOW_STATEMENT && !!tmpl.cashFlowStatementTmpl) {
        return tmpl.cashFlowStatementTmpl;
      } else {
        // Empty template. "Cannot spread"
        return [];
      }
  }

  save(state: SpreadingState, complete = false, forceSync = false, initialTemplateSelectionHasBeenMade = false, reAutonorm = false, allowDeletingInsertedRows = false, userAcceptedAttestation = false, commentMetadata: CommentSaveMetadata = null): Promise<Statement> {
    state.statement.complete = complete;
    return this._statementService.saveStatement(
      state.statement,
      state.spreadingTemplateId,
      forceSync,
      initialTemplateSelectionHasBeenMade,
      reAutonorm,
      allowDeletingInsertedRows,
      userAcceptedAttestation,
      commentMetadata
    );
  }

  changeCellValue(cell: Cell, lineItemId: number, newValue: number) {
    return Promise.resolve(true);
  }

  saveGoalSeekData(state: SpreadingState, row: Row, colIdx: number, documentFileId, solution) {
    return firstValueFrom(this._statementService.saveGoalSeekData(state, row, colIdx, documentFileId, solution));
  }
}
